import { ReactNode } from 'react';
import CourseCard from '../../components/elearning/CourseCard';
import { Course, CourseDifficulty, CourseStatus, EnrollmentStatus } from '../../types/elearning';
import { CourseProgress } from './cardReviewData';
import { exampleOptions } from './cardReviewStorage';
import { CourseStage, courseScent } from './scentViews';
import { ExampleSwitcher, FamilyBlock, ReviewFrame, columnCard } from './reviewUi';

function sampleCourse(id: string, title: string, description: string, minutes: number, moduleCount: number): Course {
  return {
    id,
    title,
    slug: id,
    description,
    objectives: [],
    prerequisites: [],
    estimatedTime: minutes,
    difficulty: CourseDifficulty.BEGINNER,
    status: CourseStatus.PUBLISHED,
    modules: Array.from({ length: moduleCount }, (_, order) => ({
      id: `${id}-${order}`,
      courseId: id,
      title: `Module ${order + 1}`,
      slug: `module-${order + 1}`,
      order,
      content: { components: [] },
      estimatedTime: Math.round(minutes / moduleCount),
      hasQuiz: false,
      createdAt: '',
      updatedAt: '',
    })),
    createdAt: '',
    updatedAt: '',
  } as unknown as Course;
}

const SAMPLE_OPEN = sampleCourse(
  'sample-open',
  'Blood Component Selection',
  'How to choose the right component for a routine transfusion order.',
  40,
  4,
);

const SAMPLE_ACTIVE = sampleCourse(
  'sample-active',
  'Transfusion Reaction Response',
  'What to do at the bedside when a reaction is suspected.',
  30,
  3,
);

function faceOf(course: Course, enrollment: CourseProgress | null) {
  const modules = course.modules?.length ?? 0;
  const done = enrollment?.status === EnrollmentStatus.COMPLETED || enrollment?.status === 'COMPLETED';
  if (done) return { stage: 'done' as CourseStage, progress: 100, finished: modules };
  if (enrollment) {
    const finished = modules > 0 ? Math.min(modules, Math.round((enrollment.progress / 100) * modules)) : 0;
    return { stage: 'active' as CourseStage, progress: enrollment.progress, finished };
  }
  return { stage: 'open' as CourseStage, progress: 0, finished: 0 };
}

export default function CourseReview({
  course,
  enrollment,
  notice,
  error,
  signedOut,
  choices,
  onChoose,
  notes,
  onNote,
}: {
  course: Course | null;
  enrollment: CourseProgress | null;
  notice: string | null;
  error: string | null;
  signedOut: boolean;
  choices: Record<string, string>;
  onChoose: (exampleId: string, optionId: string) => void;
  notes: Record<string, string>;
  onNote: (exampleId: string, note: string) => void;
}) {
  const completed = enrollment?.status === EnrollmentStatus.COMPLETED || enrollment?.status === 'COMPLETED';
  const action = completed ? 'View course' : 'Continue';
  const face = course ? faceOf(course, enrollment) : null;
  const scent = (optionId: string, frame: ReviewFrame) =>
    courseScent(course as Course, optionId, frame, face!.stage, face!.progress, face!.finished);
  const current = (view: 'grid' | 'list', frame?: ReviewFrame): ReactNode => {
    const card = <CourseCard course={course as Course} enrollment={enrollment ?? undefined} view={view} />;
    if (view === 'grid') return columnCard(frame ?? 'desktop', card);
    return card;
  };
  return (
    <FamilyBlock
      id="review-courses"
      title="Courses"
      judge="Type rail draws a course as a class with a status band. Completed turns that band crimson because the course is yours. Sample, not enrolled and Sample, in progress are made-up courses, not live records."
      destination="A course card opens the course."
      notice={signedOut ? 'Sign in to load courses.' : notice}
      error={signedOut ? null : error}
    >
      {course && (
        <>
          <ExampleSwitcher
            exampleId="courses-grid"
            label="Course catalog, grid"
            options={exampleOptions('courses-grid')}
            choice={choices['courses-grid'] ?? ''}
            onChoose={(optionId) => onChoose('courses-grid', optionId)}
            note={notes['courses-grid'] ?? ''}
            onNote={(value) => onNote('courses-grid', value)}
            render={(optionId, frame) =>
              optionId === 'current' ? current('grid', frame) : scent(optionId, frame)
            }
          />
          <ExampleSwitcher
            exampleId="courses-list"
            label="Course catalog, list"
            options={exampleOptions('courses-list')}
            choice={choices['courses-list'] ?? ''}
            onChoose={(optionId) => onChoose('courses-list', optionId)}
            note={notes['courses-list'] ?? ''}
            onNote={(value) => onNote('courses-list', value)}
            render={(optionId) => (optionId === 'current' ? current('list') : scent(optionId, 'desktop'))}
          />
          <ExampleSwitcher
            exampleId="courses-practice"
            label="Practice home"
            options={exampleOptions('courses-practice')}
            choice={choices['courses-practice'] ?? ''}
            onChoose={(optionId) => onChoose('courses-practice', optionId)}
            note={notes['courses-practice'] ?? ''}
            onNote={(value) => onNote('courses-practice', value)}
            render={(optionId) =>
              optionId === 'current' ? (
                <div className="bg-white border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between gap-4 min-h-[44px] px-4 py-3 text-sm text-gray-900">
                    <span>{course.title}</span>
                    <span className="text-xs font-medium text-primary flex-shrink-0">Continue</span>
                  </div>
                </div>
              ) : (
                scent(optionId, 'desktop')
              )
            }
          />
          <ExampleSwitcher
            exampleId="courses-sidebar"
            label="My ORBCoN"
            options={exampleOptions('courses-sidebar')}
            choice={choices['courses-sidebar'] ?? ''}
            onChoose={(optionId) => onChoose('courses-sidebar', optionId)}
            note={notes['courses-sidebar'] ?? ''}
            onNote={(value) => onNote('courses-sidebar', value)}
            render={(optionId) =>
              optionId === 'current' ? (
                <div className="min-h-[44px] py-2 px-1 text-sm text-gray-700">
                  <span>{course.title}</span>
                  <span className="block text-xs font-medium text-primary">{action}</span>
                </div>
              ) : (
                scent(optionId, 'desktop')
              )
            }
          />
        </>
      )}
      <ExampleSwitcher
        exampleId="courses-open"
        label="Sample, not enrolled"
        options={exampleOptions('courses-open')}
        choice={choices['courses-open'] ?? ''}
        onChoose={(optionId) => onChoose('courses-open', optionId)}
        note={notes['courses-open'] ?? ''}
        onNote={(value) => onNote('courses-open', value)}
        render={(optionId, frame) =>
          optionId === 'current' ? (
            columnCard(frame, <CourseCard course={SAMPLE_OPEN} view="grid" />)
          ) : (
            courseScent(SAMPLE_OPEN, optionId, frame, 'open', 0, 0)
          )
        }
      />
      <ExampleSwitcher
        exampleId="courses-progress"
        label="Sample, in progress"
        options={exampleOptions('courses-progress')}
        choice={choices['courses-progress'] ?? ''}
        onChoose={(optionId) => onChoose('courses-progress', optionId)}
        note={notes['courses-progress'] ?? ''}
        onNote={(value) => onNote('courses-progress', value)}
        render={(optionId, frame) =>
          optionId === 'current' ? (
            columnCard(frame, <CourseCard course={SAMPLE_ACTIVE} enrollment={{ status: 'IN_PROGRESS', progress: 33 }} view="grid" />)
          ) : (
            courseScent(SAMPLE_ACTIVE, optionId, frame, 'active', 33, 1)
          )
        }
      />
    </FamilyBlock>
  );
}
